/**
 * Native (Android app) notifications bridge — see labs/paziresh24-twa PushEnableActivity.
 * App builds that can turn on native push open the site with `?push=1`; the site sends the user
 * to p24push://enable, and the app reopens its launch URL with `#p24push=<token>&granted=0|1`.
 */
const CAPABLE_KEY = 'p24-native-push-capable';
const PACKAGE_KEY = 'p24-app-package';
export const ENABLED_KEY = 'p24-native-push-enabled';
const RESULT_KEY = 'p24-native-push-result';
/** Fired on window when the app hands over its answer live (TWA message channel). */
export const NATIVE_PUSH_RESULT_EVENT = 'p24push';

const storage = {
  get: (key: string) => {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key: string, value: string) => {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // private mode: the card simply shows again next time
    }
  },
};

/**
 * Same as rememberNativePushCapability, inlined in <head> by _document: the app's launch page
 * (/apphome?…&push=1) is redirected to doctor home by an inline script before React mounts, so the
 * marker and the TWA referrer must be stored before that redirect runs. The app's `#p24push=…`
 * answer is moved to sessionStorage and removed from the URL here too: the redirect drops the hash,
 * and the FCM token must not stay in the address bar (Next.js would restore it on hydration).
 * New app builds instead post the answer over the TWA message channel without any navigation;
 * only source-less messages (the app's channel, not frames such as widgets) are accepted.
 */
export const NATIVE_PUSH_INLINE_SCRIPT = String.raw`try{if(/[?&]push=1(&|$)/.test(location.search))localStorage.setItem('${CAPABLE_KEY}','1');var m=/^android-app:\/\/(com\.paziresh24\.paziresh24[\w.]*)/.exec(document.referrer);if(m)localStorage.setItem('${PACKAGE_KEY}',m[1]);if(location.hash.indexOf('#p24push=')===0){sessionStorage.setItem('${RESULT_KEY}',location.hash.slice(1));history.replaceState(history.state,'',location.pathname+location.search)}addEventListener('message',function(e){if(e.source!==null)return;var h=function(d){if(typeof d==='string'&&d.indexOf('p24push=')===0){sessionStorage.setItem('${RESULT_KEY}',d);dispatchEvent(new Event('${NATIVE_PUSH_RESULT_EVENT}'))}};if(e.ports&&e.ports[0])e.ports[0].onmessage=function(m){h(m.data)};h(e.data)})}catch(e){}`;

/** Call once per page load: remembers that we run inside a push-capable app build. */
export const rememberNativePushCapability = () => {
  if (typeof window === 'undefined') return;
  if (new URLSearchParams(window.location.search).get('push') === '1') storage.set(CAPABLE_KEY, '1');
  const app = /^android-app:\/\/([\w.]+)/.exec(document.referrer)?.[1];
  if (app?.startsWith('com.paziresh24.paziresh24')) storage.set(PACKAGE_KEY, app);
};

export const isNativePushCapable = () => typeof window !== 'undefined' && storage.get(CAPABLE_KEY) === '1';

export const isNativePushEnabled = () => typeof window !== 'undefined' && storage.get(ENABLED_KEY) === '1';

export const markNativePushEnabled = () => storage.set(ENABLED_KEY, '1');

export const clearNativePushEnabled = () => {
  try {
    window.localStorage.removeItem(ENABLED_KEY);
  } catch {
    // storage blocked: nothing to clear
  }
};

/** `code`: one-time hand-off code for when the app can't answer over the message channel. */
export const nativePushEnableUrl = (returnPath: string, code?: string) => {
  const query = `enable?return=${encodeURIComponent(returnPath)}${code ? `&code=${code}` : ''}`;
  const pkg = storage.get(PACKAGE_KEY);
  // An explicit package avoids a chooser when both the store app and a dev build are installed.
  return pkg ? `intent://${query}#Intent;scheme=p24push;package=${pkg};end` : `p24push://${query}`;
};

/** Reads and removes the app's `#p24push=…` answer (stashed by the inline script); null when there is none. */
export const takeNativePushResult = (): { token: string; granted: boolean } | null => {
  if (typeof window === 'undefined') return null;
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(RESULT_KEY);
    window.sessionStorage.removeItem(RESULT_KEY);
  } catch {
    /* storage blocked: fall back to the URL */
  }
  if (window.location.hash.startsWith('#p24push=')) {
    raw = raw ?? window.location.hash.slice(1);
    window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
  }
  if (!raw) return null;
  const params = new URLSearchParams(raw);
  return { token: params.get('p24push') ?? '', granted: params.get('granted') === '1' };
};

/** 128-bit random hex, the secret under which the app may hand the token over via the server. */
export const newHandoffCode = () =>
  Array.from(window.crypto.getRandomValues(new Uint8Array(16)), b => b.toString(16).padStart(2, '0')).join('');

/** App builds from this version on open Android's notification settings via p24push://settings. */
export const NATIVE_PUSH_SETTINGS_MIN_VERSION_CODE = 9101;

export const canOpenNativePushSettings = () => {
  if (typeof window === 'undefined' || !isNativePushCapable()) return false;
  return Number(storage.get('app:version_code')) >= NATIVE_PUSH_SETTINGS_MIN_VERSION_CODE;
};

/** Android's notification settings for this app (sound, vibration, each category). */
export const nativePushSettingsUrl = () => {
  const pkg = storage.get(PACKAGE_KEY);
  return pkg ? `intent://settings#Intent;scheme=p24push;package=${pkg};end` : 'p24push://settings';
};
